import React, { useState, useEffect, useMemo } from "react";
import { Stepper, Step } from "@material-tailwind/react";
import { IoMdBusiness } from "react-icons/io";
import { GiArchiveResearch } from "react-icons/gi";
import { MdLocationCity } from "react-icons/md";
import worldMap from "../assets/images/Blue-Map.jpg";
import { PiCertificateBold } from "react-icons/pi";
import { PiTrendUpBold } from "react-icons/pi";
import { GrDocumentText, GrTechnology } from "react-icons/gr";
import { FaArrowRightFromBracket } from "react-icons/fa6";
import { PiHandTap } from "react-icons/pi";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import {
  montenegroHero,
  netherlandsHero,
  canadaHero,
  saudiArabiaHero,
  czechHero,
  lithuaniaHero,
  uaeHero,
  ukHero,
  polandHero,
  romaniaHero,
  serbiaHero,
  ukraineHero,
  swedenHero,
  generalHero,
} from "../assets/images/Countries-Hero-Images";
import {
  FaUsersCog,
  FaGlobe,
  FaCheckCircle,
  FaClock,
  FaAward,
  FaArrowRight,
  FaBriefcase,
  FaBolt,
  FaChevronRight,
  FaHandPointRight,
  FaPlay,
} from "react-icons/fa";
import { Card, CardContent } from "../utils/Card";
import { Button } from "../utils/Button";
import { UpperHeading } from "../utils";
import { FaArrowTrendUp } from "react-icons/fa6";

/* ── Hero image map (module-level so it is never recreated) ── */
const HERO_MAP = {
  netherlands: netherlandsHero,
  canada: canadaHero,
  "united kingdom": ukHero,
  "czech republic": czechHero,
  lithuania: lithuaniaHero,
  montenegro: montenegroHero,
  poland: polandHero,
  "united arab emirates": uaeHero,
  "saudi arabia": saudiArabiaHero,
  romania: romaniaHero,
  serbia: serbiaHero,
  ukraine: ukraineHero,
  sweden: swedenHero,
  general: generalHero,
};

const Home = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const country = localStorage.getItem("destination");
  const [isLastStep, setIsLastStep] = useState(false);
  const [stepperClicked, setStepperClicked] = useState(false);

  /* Resolve hero image BEFORE first paint so Lighthouse / browser can discover it */
  const heroSrc = useMemo(() => {
    return HERO_MAP[country?.toLowerCase()] || generalHero;
  }, [country]);

  /* Auto-advance stepper */
  useEffect(() => {
    let timer;
    if (!stepperClicked) {
      timer = setTimeout(() => {
        setActiveStep((prev) => (prev < 3 ? prev + 1 : 0));
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [activeStep, stepperClicked]);

  const steps = [
    {
      id: 0,
      Heading: "Sourcing & Recruitment Services",
      content:
        "Identify the right opportunities and connect you with employers who need your skills — whether you’re a Highly Skilled Professional or an experienced Skilled Tradesperson.",
    },
    {
      id: 1,
      Heading: "Hiring & Authorization Services",
      content:
        "Handle every document, contract, and government filing. We manage work permit applications, LMIA/Express Entry support (Canada), employment visas (UAE), and full compliance so you don’t have to worry.",
    },
    {
      id: 2,
      Heading: "Relocation & Legalization Services",
      content:
        "From airport pickup and housing to Emirates ID, medical tests, bank accounts, and local registrations — we take care of the details so you can focus on your new life.",
    },
    {
      id: 3,
      Heading: "Onboarding & Deployment Services",
      content:
        "Seamless integration with your new employer and destination. Ongoing support for extensions, family visas, and long-term settlement.",
    },
  ];

  const cards = [
    {
      icon: (
        <MdLocationCity className="text-5xl text-[#DA7C09] group-hover:text-myBlue transition-colors" />
      ),
      heading: "Strategic Location and Connectivity",
      text: "Excellent connectivity with major markets, advanced infrastructure, and robust digital networks.",
    },
    {
      icon: (
        <IoMdBusiness className="text-5xl text-[#DA7C09] group-hover:text-myBlue transition-colors" />
      ),
      heading: "Supportive Business Environment",
      text: "Active government support, vibrant startup ecosystem, and diverse funding opportunities.",
    },
    {
      icon: (
        <FaUsersCog className="text-5xl text-[#DA7C09] group-hover:text-myBlue transition-colors" />
      ),
      heading: "Highly Skilled Workforce",
      text: "Strong education system, multilingual talent, and a culturally diverse work environment.",
    },
    {
      icon: (
        <GrTechnology className="text-5xl text-[#DA7C09] group-hover:text-myBlue transition-colors" />
      ),
      heading: "Thriving Community",
      text: "Industry clusters, leading companies, and a collaborative community with regular meetups and conferences.",
    },
    {
      icon: (
        <GiArchiveResearch className="text-5xl text-[#DA7C09] group-hover:text-myBlue transition-colors" />
      ),
      heading: "Innovation and Research",
      text: "Significant R&D investments and collaboration between academia and industry, driving technological advancements.",
    },
    {
      icon: (
        <FaGlobe className="text-5xl text-[#DA7C09] group-hover:text-myBlue transition-colors" />
      ),
      heading: "Quality of Life",
      text: "Excellent work-life balance, high standard of living, and commitment to sustainability.",
    },
  ];

  return (
    <div className="home">
      <Helmet>
        {/* Primary SEO */}
        <title>
          Work Permit Consultancy | Trusted Work Permit & Immigration Experts
        </title>

        <meta
          name="description"
          content="Work Permit Consultancy helps professionals secure global work permits with expert immigration guidance, fast processing, and a proven success rate."
        />

        {/* Canonical */}
        <link rel="canonical" href="https://www.workpermitconsultancy.com/" />

        {/* Open Graph */}
        <meta
          property="og:title"
          content="Work Permit Consultancy | Trusted Work Permit & Immigration Experts"
        />
        <meta
          property="og:description"
          content="Secure your work permit with expert immigration consultants. Transparent process, fast approvals, and end-to-end support."
        />
        <meta
          property="og:url"
          content="https://www.workpermitconsultancy.com/"
        />
        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta
          name="twitter:title"
          content="Work Permit Consultancy | Immigration Experts"
        />
        <meta
          name="twitter:description"
          content="Trusted consultants helping professionals obtain work permits worldwide."
        />

        {/* Preload the LCP hero image so the browser starts downloading it immediately */}
        <link rel="preload" as="image" href={heroSrc} fetchPriority="high" />
      </Helmet>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Work Permit Consultancy",
          url: "https://www.workpermitconsultancy.com",
          logo: "https://www.workpermitconsultancy.com/web-app-manifest-512x512.png",
          description:
            "Expert work permit and immigration consultancy services for professionals seeking to work abroad.",
          contactPoint: {
            "@type": "ContactPoint",
            telephone: "+971-52-755-9917",
            contactType: "customer service",
            email: "info@workpermitconsultancy.com",
          },
          sameAs: ["https://nl.linkedin.com/company/work-permit-consultancy"],
        })}
      </script>

      {/* ====================== HERO ====================== */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <img
          src={heroSrc}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          fetchPriority="high"
          decoding="async"
        />

        {!country && (
          <div className="absolute inset-0 bg-[#2d202c1a]/10 z-[1]" />
        )}

        <div className="container mx-auto px-4 lg:px-8 relative z-10 py-12 lg:py-16">
          <div
            className={
              !country?.toLowerCase()
                ? "grid lg:grid-cols-2 gap-12 lg:gap-16 items-center bg-transparent backdrop-blur-[2px]"
                : "grid lg:grid-cols-2 gap-12 lg:gap-16 items-center"
            }
          >
            {/* Left Column */}
            <div className="text-center lg:text-left space-y-8">
              <div className="flex justify-center lg:justify-start text-shadow-black">
                <span className="inline-flex items-center bg-white/30 border border-white/30 backdrop-blur-sm text-white px-4 py-1.5 rounded-full text-sm font-medium">
                  <FaBolt
                    className={`mr-1.5 h-4 w-4 ${
                      !country?.toLowerCase() ? "text-[#5eb8e8]" : "text-myBlue"
                    }`}
                  />
                  #1 Work Permit Specialists
                </span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight">
                <span className="text-white text-shadow-black">
                  Secure Your
                </span>
                <span
                  className={
                    !country?.toLowerCase()
                      ? "block text-blue-200 drop-shadow-[6px_8px_4px_rgba(0,0,0,0.4)]"
                      : "block bg-gradient-to-r from-[#da7c09] to-orange-300 bg-clip-text text-transparent drop-shadow-[6px_8px_4px_rgba(0,0,0,0.4)]"
                  }
                >
                  Work Permit
                </span>
                <span
                  className={`font-semibold ${
                    !country?.toLowerCase() ? "text-white" : "text-blue-200"
                  } block text-3xl md:text-4xl lg:text-5xl mt-2 text-shadow-black `}
                >
                  Today
                </span>
              </h1>

              <p className="text-lg md:text-xl lg:text-2xl text-white leading-relaxed max-w-2xl mx-auto lg:mx-0 text-shadow-black-2">
                We guide you through{" "}
                <span
                  className={`font-semibold ${
                    !country?.toLowerCase() ? "text-[#5eb8e8]" : "text-myGolden"
                  }`}
                >
                  work permits
                </span>{" "}
                and{" "}
                <span
                  className={`font-semibold ${
                    !country?.toLowerCase() ? "text-[#5eb8e8]" : "text-myGolden"
                  }`}
                >
                  immigration
                </span>{" "}
                with expert support, making the process simple, transparent, and
                stress-free.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                {/* Primary CTA */}
                <button
                  onClick={() => navigate("/apply-now")}
                  className={`flex items-center justify-center text-white font-medium text-lg px-8 py-4 rounded-lg shadow-xl transition-all duration-300 group drop-shadow-[6px_8px_4px_rgba(31,31,31,0.4)] ${
                    !country?.toLowerCase()
                      ? "bg-gradient-to-r from-[#084b72] to-[#1a8bc4] hover:from-[#063a5a] hover:to-[#0d6ea8]"
                      : "bg-gradient-to-r from-[#da7c09] to-orange-600 hover:from-[#c26d08] hover:to-orange-700"
                  }`}
                >
                  Start your process
                  <PiHandTap className="ml-2 h-7 w-7 group-hover:translate-x-1 transition-transform" />
                </button>

                {/* Secondary button */}
                <button
                  onClick={() => navigate("/about-us")}
                  className={`flex items-center border-2 text-white bg-white/10 backdrop-blur-sm font-medium text-lg px-8 py-4 rounded-lg transition-all duration-300 group drop-shadow-[6px_8px_4px_rgba(31,31,31,0.5)] ${
                    !country?.toLowerCase()
                      ? "border-[#5eb8e8]/60 hover:bg-[#084b72]/30"
                      : "border-[#da7c09]/50 hover:bg-[#da7c09]/20"
                  }`}
                >
                  <FaArrowRightFromBracket className="mr-4 h-7 w-7" />
                  Learn more
                </button>
              </div>

              {/* Trust Indicators */}
              <div
                className={`grid grid-cols-3 gap-6 pt-8 border-t ${
                  !country?.toLowerCase()
                    ? "border-[#5eb8e8]/70"
                    : "border-[#da7c09]/70"
                }`}
              >
                {[
                  { num: "1000+", label: "Work Permits Approved" },
                  { num: "99%", label: "Success Rate" },
                  { num: "106+", label: "Consultants & Experts" },
                ].map((item, i) => (
                  <div key={i} className="text-center">
                    <div
                      className={`text-2xl md:text-3xl font-bold text-shadow-black ${
                        !country?.toLowerCase()
                          ? "text-[#ffff]"
                          : "text-[#da7c09]"
                      }`}
                    >
                      {item.num}
                    </div>
                    <div className="text-xs md:text-sm text-blue-200 mt-1 text-shadow-black-1">
                      {item.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column – Status Card */}
            <div className="relative mt-12 lg:mt-0 flex justify-center lg:justify-end">
              <div className="relative z-10 w-full max-w-md">
                <div
                  className={`${
                    !country?.toLowerCase()
                      ? "border-[#5eb8e8]/50 bg-white/10 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border-2"
                      : "border-myGolden/60 bg-white/10 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border-2 "
                  }`}
                >
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-white font-semibold text-lg">
                      Work Permit Status
                    </h3>
                    <span className="bg-green-500/20 text-[#80de84] border border-green-400/30 px-3 py-1 rounded-md text-sm">
                      Approved
                    </span>
                  </div>

                  <div className="space-y-4">
                    {[
                      "Application Submitted",
                      "Document Verification",
                      "Government Review",
                      "Permit Issued",
                    ].map((label, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between text-white"
                      >
                        <span className={i === 3 ? "font-semibold" : ""}>
                          {label}
                        </span>
                        <FaCheckCircle className="h-5 w-5 text-[#80de84]/85" />
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 p-4 bg-gradient-to-r from-green-500/20 to-blue-500/20 rounded-xl border border-green-400/20">
                    <div className="flex items-center space-x-3">
                      <div className="bg-green-500 p-2 rounded-full">
                        <FaAward className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <div className="text-white font-semibold">
                          Congratulations!
                        </div>
                        <div className="text-[#80de84] text-sm">
                          Work permit approved in 18 days
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Arrow */}
              <div
                className={`absolute -top-8 hidden sm:block sm:right-[0rem] md:right-[6rem] lg:-right-6 z-10 p-4 rounded-2xl shadow-2xl animate-bounce ${
                  !country?.toLowerCase()
                    ? "bg-gradient-to-r from-[#084b72] to-[#1a8bc4]"
                    : "bg-gradient-to-r from-[#da7c09] to-orange-600"
                }`}
              >
                <FaArrowTrendUp className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====================== HOW IT WORKS ====================== */}
      <section className="bg-[#F2F7FF] py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8 text-center space-y-6">
          <UpperHeading text="Our Procedure" />
          <h2 className="text-3xl md:text-4xl font-playfairDisplay font-semibold text-myBlue">
            Our Process, Your Pathway
          </h2>
          <p className="max-w-3xl mx-auto text-gray-700">
            At Work Permit Consultancy, we simplify the entire journey. Whether
            you are applying for a <strong>Highly Skilled Professional</strong>{" "}
            work permit or a <strong>Skilled Trade & Workforce</strong> visa,
            our expert team guides you from initial consultation through
            document preparation, application submission, and final approval.
          </p>
        </div>

        <div className="container mx-auto px-4 lg:px-8 mt-12">
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-10 max-w-5xl mx-auto">
            <Stepper
              activeStep={activeStep}
              activeLineClassName="!bg-myBlue"
              className="w-full"
              isLastStep={(v) => setIsLastStep(v)}
            >
              {steps.map((s) => (
                <Step
                  key={s.id}
                  onClick={() => {
                    setStepperClicked(true);
                    setActiveStep(s.id);
                  }}
                  activeClassName="!bg-myBlue text-white"
                  completedClassName="!bg-myBlue text-white"
                >
                  {s.id + 1}
                </Step>
              ))}
            </Stepper>

            <div className="mt-8">
              {steps.map((s) => (
                <div
                  key={s.id}
                  className={`transition-opacity duration-300 ${
                    activeStep === s.id ? "block" : "hidden"
                  }`}
                  onMouseEnter={() => setStepperClicked(true)}
                  onMouseLeave={() => setStepperClicked(false)}
                >
                  <div className="bg-[#F2F7FF] rounded-xl p-6 text-center">
                    <h3 className="text-lg md:text-xl font-semibold text-myBlue mb-2">
                      {s.Heading}
                    </h3>
                    <p className="text-sm md:text-base text-gray-700">
                      {s.content}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ====================== OUR PROMISE ====================== */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8 text-center space-y-6">
          <UpperHeading text="Our Promise" />
          <h2 className="text-3xl md:text-4xl font-playfairDisplay font-semibold text-myBlue">
            Your Trusted Path to Work Permits
          </h2>
          <p className="max-w-3xl mx-auto text-gray-700">
            Choose us for trusted guidance and end-to-end support. From personal
            consultation and document preparation to application submissions and
            post-approval assistance, our expert team ensures your journey is
            seamless, efficient, and stress-free.
          </p>
        </div>

        <div className="container mx-auto px-4 lg:px-8 mt-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Card 1 */}
            <Card className="group hover:shadow-2xl transition-all duration-500 border-0 shadow-lg bg-gradient-to-br from-white to-[#084b72]/5 overflow-hidden relative">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#084b72] to-[#da7c09]" />
              <CardContent className="p-8">
                <div className="bg-gradient-to-r from-[#084b72] to-[#0a5a87] w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <FaBriefcase className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-myBlue">
                  Experience And Expertise
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Years of proven expertise in work permits for Canada, UAE,
                  Europe and beyond. Whether you’re a Highly Skilled
                  Professional or a Skilled Tradesperson, we deliver accurate,
                  up-to-date guidance tailored to your goals.
                </p>
                <Button
                  onClick={() => navigate("/about-us")}
                  className="w-full bg-gradient-to-r from-[#265a79] to-[#da994a]  group-hover:bg-gradient-to-r group-hover:from-[#084b72] group-hover:to-[#da7c09] transition-all duration-300"
                >
                  Learn More
                  <FaChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>

            {/* Card 2 */}
            <Card className="group hover:shadow-2xl transition-all duration-500 border-0 shadow-lg bg-gradient-to-br from-white to-[#084b72]/5 overflow-hidden relative">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#da7c09] to-orange-600" />
              <CardContent className="p-8">
                <div className="bg-gradient-to-r from-[#da7c09] to-orange-600 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <PiTrendUpBold className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-myBlue">
                  Safe And Secure
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Your personal data and documents are fully protected with
                  strict confidentiality. We ensure complete security and
                  transparency throughout your Highly Skilled Professional or
                  Skilled Tradesperson work permit journey.
                </p>
                <Button
                  onClick={() => navigate("/about-us")}
                  className="w-full bg-gradient-to-r from-[#265a79] to-[#da994a]   group-hover:bg-gradient-to-r group-hover:from-[#084b72] group-hover:to-[#da7c09] transition-all duration-300"
                >
                  Learn More
                  <FaChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>

            {/* Card 3 */}
            <Card className="group hover:shadow-2xl transition-all duration-500 border-0 shadow-lg bg-gradient-to-br from-white to-[#084b72]/5 overflow-hidden relative md:col-span-2 lg:col-span-1">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 to-[#da7c09]" />
              <CardContent className="p-8">
                <div className="bg-gradient-to-r from-amber-500 to-[#da7c09] w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <FaClock className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-myBlue">
                  Efficient and Transparent
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  We deliver fast processing with clear timelines and regular
                  updates. From initial consultation to final approval, our
                  streamlined process makes securing your work permit in Canada,
                  UAE, or Europe simple and stress-free.
                </p>
                <Button
                  onClick={() => navigate("/about-us")}
                  className="w-full bg-gradient-to-r from-[#265a79] to-[#da994a]   group-hover:bg-gradient-to-r group-hover:from-[#084b72] group-hover:to-[#da7c09] transition-all duration-300"
                >
                  Learn More
                  <FaChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ====================== CALL TO ACTION ====================== */}
      <section className="relative py-20 lg:py-28 text-white overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={worldMap}
            alt="World Map"
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
        </div>

        <div className="container mx-auto px-4 lg:px-8 text-center relative z-10 space-y-8">
          <UpperHeading
            className="text-white border-white"
            text="Start Your Journey"
          />
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold">
            Ready to Secure Your Work Permit?
          </h2>
          <p className="max-w-3xl mx-auto text-lg md:text-xl text-blue-100 leading-relaxed">
            Join over 1000 professionals who've successfully obtained their work
            permits through our expert guidance. Get started with a free
            assessment today.
          </p>

          <div className="flex flex-col lg:flex-row gap-4 justify-center items-center">
            <Button
              onClick={() => navigate("/apply-now")}
              size="lg"
              className=" bg-gradient-to-r from-myBlue to-[#db8317] hover:gradient-to-r hover:from-[#db8317] hover:to-myBlue text-white shadow-2xl flex items-center"
            >
              <GrDocumentText className="mr-2 h-4 w-4" />
              Initiate your application
              <PiHandTap className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>

            <Button
              onClick={() => navigate("/about-us")}
              size="lg"
              variant="outline"
              className="border-2 border-white/50 text-white hover:bg-white/10 bg-black/5 backdrop-blur-sm flex items-center"
            >
              <FaHandPointRight className="mr-2 h-5 w-5" />
              Know more
            </Button>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mt-12">
            {[
              { icon: PiCertificateBold, text: "Efficient & Reliable" },
              { icon: FaClock, text: "24/7 Support" },
              { icon: FaAward, text: "Licensed Consultants" },
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-center justify-center space-x-3"
              >
                <item.icon className="h-6 w-6 text-[#da7c09]" />
                <span className="text-blue-100">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====================== WHY [COUNTRY] ====================== */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4 lg:px-8 text-center space-y-6">
          <h2 className="text-3xl md:text-4xl font-playfairDisplay font-semibold text-myBlue capitalize">
            {country ? `Why ${country}` : "Why Work Abroad with Us"}
          </h2>
          <p className="text-gray-700 max-w-3xl mx-auto">
            {country
              ? `Whether you’re dreaming of vibrant cities and world-class opportunities in ${country}, or exploring other global destinations, the right work permit opens the door. We help you turn that dream into reality.`
              : `Whether you’re dreaming of vibrant cities, world-class career opportunities, or a fresh start in a new country, the right work permit opens the door. We help professionals and skilled tradespeople turn that dream into reality — across Canada, UAE, Europe and beyond.`}
          </p>
        </div>

        <div className="container mx-auto px-4 lg:px-8 mt-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cards.map((card, i) => (
              <div
                key={i}
                className="group flex items-start p-6 bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300"
              >
                <div className="mr-5 flex-shrink-0">{card.icon}</div>
                <div className="text-left">
                  <h3 className="font-bold text-lg text-myBlue mb-1">
                    {card.heading}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {card.text}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
